import React, { useState, useRef, useEffect } from 'react';
import styles from '../style/InteractivePlot.module.css';

const InteractivePlotContainer = ({ src, onInteraction, viewState, interactionMode }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [startPan, setStartPan] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    time: null,
    valY1: null,
    valY2: null
  });

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    setIsDragging(false);
    setSelectionBox(null);
    setStartPan(null);
    setTooltip(prev => ({ ...prev, visible: false }));
  }, [src, interactionMode]);

  const getRelativeCoords = (e) => {
    if (!imageRef.current) return null;
    const rect = imageRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const formatTime = (val) => {
    if (typeof val !== 'number') return '';
    const date = new Date(Math.round(val < 10000000000 ? val * 1000 : val));
    const timeZone = viewState?.timezone || 'UTC';
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone
    });
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0 || !viewState || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      return;
    }

    const coords = getRelativeCoords(e);
    if (!coords) return;

    setIsDragging(true);
    setTooltip(prev => ({ ...prev, visible: false }));

    if (interactionMode === 'pan') {
      setStartPan({ x: e.clientX, y: e.clientY });
    } else {
      setSelectionBox({ x1: coords.x, y1: coords.y, x2: coords.x, y2: coords.y });
    }
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!viewState || !imageRef.current) return;

    if (!isDragging) {
      const coords = getRelativeCoords(e);
      if (!coords || !viewState.ax_bbox) {
        setTooltip(prev => ({ ...prev, visible: false }));
        return;
      }

      const rect = imageRef.current.getBoundingClientRect();
      const clientWidth = rect.width;
      const clientHeight = rect.height;
      const [bboxX, bboxY, bboxW, bboxH] = viewState.ax_bbox;

      const axesLeftPx = bboxX * clientWidth;
      const axesWidthPx = bboxW * clientWidth;
      const axesTopPx = (1 - (bboxY + bboxH)) * clientHeight;
      const axesHeightPx = bboxH * clientHeight;

      if (
        coords.x >= axesLeftPx &&
        coords.x <= axesLeftPx + axesWidthPx &&
        coords.y >= axesTopPx &&
        coords.y <= axesTopPx + axesHeightPx
      ) {
        const ratioX = (coords.x - axesLeftPx) / axesWidthPx;
        const ratioY = 1 - ((coords.y - axesTopPx) / axesHeightPx);
        const { xlim, ylim, y2lim } = viewState;

        const dataX = xlim[0] + ratioX * (xlim[1] - xlim[0]);
        const dataY1 = ylim[0] + ratioY * (ylim[1] - ylim[0]);
        let dataY2 = null;
        if (y2lim) {
          dataY2 = y2lim[0] + ratioY * (y2lim[1] - y2lim[0]);
        }

        setTooltip({
          visible: true,
          x: coords.x,
          y: coords.y,
          time: dataX,
          valY1: dataY1,
          valY2: dataY2
        });
      } else {
        setTooltip(prev => ({ ...prev, visible: false }));
      }
    }

    if (!isDragging) return;

    if (interactionMode === 'zoom') {
      const coords = getRelativeCoords(e);
      if (!coords) return;
      const rect = imageRef.current.getBoundingClientRect();
      const clampedX = Math.max(0, Math.min(coords.x, rect.width));
      const clampedY = Math.max(0, Math.min(coords.y, rect.height));
      setSelectionBox(prev => ({ ...prev, x2: clampedX, y2: clampedY }));
    } else if (interactionMode === 'pan' && startPan) {
      if (!viewState.ax_bbox) return;

      const dx = e.clientX - startPan.x;
      const dy = e.clientY - startPan.y;

      const [, , bboxW, bboxH] = viewState.ax_bbox;
      const rect = imageRef.current.getBoundingClientRect();
      const clientWidth = rect.width;
      const clientHeight = rect.height;
      const axesWidthPx = bboxW * clientWidth;
      const axesHeightPx = bboxH * clientHeight;

      const { xlim, ylim, y2lim } = viewState;
      const xRange = xlim[1] - xlim[0];
      const yRange = ylim[1] - ylim[0];

      const xShift = (dx / axesWidthPx) * xRange;
      const yShift = (dy / axesHeightPx) * yRange;

      const newXlim = [xlim[0] - xShift, xlim[1] - xShift];
      const newYlim = [ylim[0] + yShift, ylim[1] + yShift];

      const newViewState = { ...viewState, xlim: newXlim, ylim: newYlim };
      if (y2lim) {
        const y2Range = y2lim[1] - y2lim[0];
        const y2Shift = (dy / axesHeightPx) * y2Range;
        newViewState.y2lim = [y2lim[0] + y2Shift, y2lim[1] + y2Shift];
      }

      onInteraction(newViewState);
      setStartPan({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    if (interactionMode === 'zoom' && isDragging && selectionBox) {
      const rect = imageRef.current.getBoundingClientRect();
      const clientWidth = rect.width;
      const clientHeight = rect.height;
      const { x1, y1, x2, y2 } = selectionBox;

      if (!viewState.ax_bbox) {
        console.error('Zoom failed: ax_bbox not found.');
        return;
      }

      if (Math.abs(x1 - x2) > 5 && Math.abs(y1 - y2) > 5) {
        const [bboxX, bboxY, bboxW, bboxH] = viewState.ax_bbox;

        const axesLeftPx = bboxX * clientWidth;
        const axesWidthPx = bboxW * clientWidth;
        const axesTopPx = (1 - (bboxY + bboxH)) * clientHeight;
        const axesHeightPx = bboxH * clientHeight;

        const startX_ratio = (Math.min(x1, x2) - axesLeftPx) / axesWidthPx;
        const endX_ratio = (Math.max(x1, x2) - axesLeftPx) / axesWidthPx;
        const startY_ratio = 1 - ((Math.max(y1, y2) - axesTopPx) / axesHeightPx);
        const endY_ratio = 1 - ((Math.min(y1, y2) - axesTopPx) / axesHeightPx);

        const clamped_startX = Math.max(0, Math.min(1, startX_ratio));
        const clamped_endX = Math.max(0, Math.min(1, endX_ratio));
        const clamped_startY = Math.max(0, Math.min(1, startY_ratio));
        const clamped_endY = Math.max(0, Math.min(1, endY_ratio));

        if (clamped_endX <= clamped_startX || clamped_endY <= clamped_startY) return;

        const { xlim, ylim, y2lim } = viewState;
        const xRange = xlim[1] - xlim[0];
        const yRange = ylim[1] - ylim[0];

        const newXlim = [xlim[0] + clamped_startX * xRange, xlim[0] + clamped_endX * xRange];
        const newYlim = [ylim[0] + clamped_startY * yRange, ylim[0] + clamped_endY * yRange];

        const newViewState = { ...viewState, xlim: newXlim, ylim: newYlim };
        if (y2lim) {
          const y2Range = y2lim[1] - y2lim[0];
          newViewState.y2lim = [y2lim[0] + clamped_startY * y2Range, y2lim[0] + clamped_endY * y2Range];
        }
        onInteraction(newViewState);
      }
    }
    setIsDragging(false);
    setSelectionBox(null);
    setStartPan(null);
  };

  const handleMouseLeave = () => {
    handleMouseUp();
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const cursorStyle = interactionMode === 'pan' ? (isDragging ? 'grabbing' : 'grab') : 'crosshair';

  const containerWidth = containerRef.current?.clientWidth || 0;
  const containerHeight = containerRef.current?.clientHeight || 0;
  const tooltipWidth = viewState?.y2lim ? 170 : 132;
  const tooltipHeight = viewState?.y2lim ? 78 : 62;
  const tooltipLeft = Math.max(12, Math.min(tooltip.x + 18, Math.max(12, containerWidth - tooltipWidth - 12)));
  const tooltipTop = Math.max(12, Math.min(tooltip.y + 18, Math.max(12, containerHeight - tooltipHeight - 12)));

  return (
    <div
      ref={containerRef}
      className={styles.plotInteractionWrapper}
      style={{ cursor: cursorStyle }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <img ref={imageRef} src={src} alt="Analysis Plot" style={{ display: 'block', maxWidth: '100%' }} />

      {interactionMode === 'zoom' && isDragging && selectionBox && (
        <div
          className={styles.selectionBox}
          style={{
            left: (imageRef.current?.offsetLeft || 0) + Math.min(selectionBox.x1, selectionBox.x2),
            top: (imageRef.current?.offsetTop || 0) + Math.min(selectionBox.y1, selectionBox.y2),
            width: Math.abs(selectionBox.x1 - selectionBox.x2),
            height: Math.abs(selectionBox.y1 - selectionBox.y2),
          }}
        />
      )}

      {tooltip.visible && (
        <div
          className={styles.cursorTooltip}
          style={{
            left: tooltipLeft,
            top: tooltipTop,
          }}
        >
          <div className={styles.tooltipHeader}>
            {formatTime(tooltip.time)}
          </div>

          <div className={styles.tooltipValues}>
            {viewState.y2lim ? (
              <>
                <div className={styles.tooltipPrimaryValue}>
                  <span className={styles.tooltipLabel}>L:</span>
                  {tooltip.valY1?.toFixed(2)}
                </div>
                <div className={styles.tooltipSecondaryValue}>
                  <span className={styles.tooltipLabel}>R:</span>
                  {tooltip.valY2?.toFixed(2)}
                </div>
              </>
            ) : (
              <div className={styles.tooltipNeutralValue}>
                <span className={styles.tooltipLabel}>Value:</span>
                {tooltip.valY1?.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractivePlotContainer;
